import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BackLink } from "@/components/back-link";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { HomeOrBusinessButtons, type CustomerType } from "@/components/customer-home-or-business";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageSpinner } from "@/components/ui/spinner";
import { ArrowLeft, Save } from "lucide-react";

interface CustomerDetail {
    id: string;
    company_name: string;
    customer_type: string | null;
    is_active: boolean;
    payment_profile?: string;
    net_terms_days?: number | null;
    tax_status?: string;
    tax_exempt_reason?: string | null;
    exemption_cert_ref?: string | null;
    billing_address_line1?: string | null;
    billing_address_line2?: string | null;
    billing_city?: string | null;
    billing_state?: string | null;
    billing_zip?: string | null;
}

const PAYMENT_PROFILE_OPTIONS = [
    { value: "pay_on_invoice", label: "Pay on invoice (card or ACH link)" },
    { value: "auto_charge", label: "Auto-charge (card on file)" },
    { value: "card_on_file_manual", label: "Card on file, manual charge" },
    { value: "net_terms", label: "Net terms (invoice on terms)" },
] as const;

const NET_TERMS_OPTIONS = [15, 30, 45] as const;

export default function CustomerFormPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const isEdit = !!id && id !== "new";

    const [companyName, setCompanyName] = useState("");
    // Home or business. No default (Corbin 2026-10-28): blank stays blank
    // until someone picks.
    const [customerType, setCustomerType] = useState<CustomerType | null>(null);
    const [typeError, setTypeError] = useState<string | null>(null);
    const [isActive, setIsActive] = useState(true);
    const [paymentProfile, setPaymentProfile] = useState("pay_on_invoice");
    const [netTermsDays, setNetTermsDays] = useState(30);
    const [taxStatus, setTaxStatus] = useState("unknown");
    const [taxExemptReason, setTaxExemptReason] = useState("");
    const [exemptionCertRef, setExemptionCertRef] = useState("");
    const [billingLine1, setBillingLine1] = useState("");
    const [billingLine2, setBillingLine2] = useState("");
    const [billingCity, setBillingCity] = useState("");
    const [billingState, setBillingState] = useState("");
    const [billingZip, setBillingZip] = useState("");

    // Use the shared { customer } response shape so this query is cache-key
    // compatible with the customer-detail page and breadcrumb (both also key
    // off ["customer", id]). Unwrapping here previously caused whichever writer
    // won the shared cache to feed the form the wrong shape, blanking fields.
    const { data: existing, isLoading } = useQuery({
        queryKey: ["customer", id],
        queryFn: () => api.get<{ customer: CustomerDetail }>(`/api/customers/${id}`),
        enabled: isEdit,
    });

    useEffect(() => {
        const customer = existing?.customer;
        if (customer) {
            setCompanyName(customer.company_name ?? "");
            setCustomerType(
                customer.customer_type === "residential" || customer.customer_type === "commercial"
                    ? customer.customer_type
                    : null,
            );
            setIsActive(customer.is_active ?? true);
            setPaymentProfile(customer.payment_profile ?? "pay_on_invoice");
            setNetTermsDays(customer.net_terms_days ?? 30);
            setTaxStatus(customer.tax_status ?? "unknown");
            setTaxExemptReason(customer.tax_exempt_reason ?? "");
            setExemptionCertRef(customer.exemption_cert_ref ?? "");
            setBillingLine1(customer.billing_address_line1 ?? "");
            setBillingLine2(customer.billing_address_line2 ?? "");
            setBillingCity(customer.billing_city ?? "");
            setBillingState(customer.billing_state ?? "");
            setBillingZip(customer.billing_zip ?? "");
        }
    }, [existing]);

    const mutation = useMutation<{ customer?: CustomerDetail; id?: string }, Error, Record<string, unknown>>({
        mutationFn: async (payload) => {
            if (isEdit) {
                const r = await api.patch<{ customer: CustomerDetail }>(`/api/customers/${id}`, payload);
                // Billing address lives on a dedicated route; persist it in the
                // same save so the edit page is a single Save for the office.
                await api.patch(`/api/customers/${id}/billing`, {
                    billingAddressLine1: billingLine1.trim() || null,
                    billingAddressLine2: billingLine2.trim() || null,
                    billingCity: billingCity.trim() || null,
                    billingState: billingState.trim() || null,
                    billingZip: billingZip.trim() || null,
                });
                return { customer: r.customer };
            }
            const r = await api.post<{ id: string }>("/api/customers", payload);
            return { id: r.id };
        },
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ["customers"] });
            if (isEdit) {
                qc.invalidateQueries({ queryKey: ["customer", id] });
                navigate(`/customers/${id}`);
            } else if (data.id) {
                navigate(`/customers/${data.id}`);
            }
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // A new customer must be marked Home or Business. On edit, a customer
        // that is still blank may be saved blank (the header keeps asking).
        if (!isEdit && !customerType) {
            setTypeError("Pick Home or Business.");
            return;
        }
        mutation.mutate({
            companyName,
            ...(customerType ? { customerType } : {}),
            isActive,
            paymentProfile,
            netTermsDays: paymentProfile === "net_terms" ? netTermsDays : null,
            // Tax fields live on the PATCH route only (edit mode).
            ...(isEdit
                ? {
                      taxStatus,
                      taxExemptReason: taxStatus === "exempt" ? taxExemptReason.trim() || null : null,
                      exemptionCertRef: taxStatus === "exempt" ? exemptionCertRef.trim() || null : null,
                  }
                : {}),
        });
    };

    if (isEdit && isLoading) return <PageSpinner />;

    return (
        <div className="space-y-6 max-w-2xl mx-auto">
            <div>
                <BackLink
                    fallback="/customers"
                    fallbackLabel="Back to customers"
                    className="text-sm text-shell-foreground/70 hover:text-shell-foreground inline-flex items-center gap-1 mb-2"
                    icon={<ArrowLeft className="h-3 w-3" />}
                />
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground">
                    {isEdit ? "Edit Customer" : "New Customer"}
                </h1>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Customer Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                        <div className="space-y-1.5">
                            <label htmlFor="companyName" className="text-sm font-medium">
                                Company Name <span className="text-destructive">*</span>
                            </label>
                            <Input
                                id="companyName"
                                required
                                placeholder="Acme Corp"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                            />
                        </div>

                        <HomeOrBusinessButtons
                            value={customerType}
                            onChange={(v) => {
                                setCustomerType(v);
                                setTypeError(null);
                            }}
                            error={typeError}
                        />

                        <div className="space-y-1.5">
                            <label htmlFor="paymentProfile" className="text-sm font-medium">
                                Payment Profile <span className="text-destructive">*</span>
                            </label>
                            <Select
                                id="paymentProfile"
                                value={paymentProfile}
                                onChange={(e) => setPaymentProfile(e.target.value)}
                            >
                                {PAYMENT_PROFILE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Select>
                            <p className="text-xs text-shell-foreground/60">
                                Controls how this account is billed and whether a card on file is required before
                                dispatch.
                            </p>
                        </div>

                        {paymentProfile === "net_terms" && (
                            <div className="space-y-1.5">
                                <label htmlFor="netTermsDays" className="text-sm font-medium">
                                    Net Terms <span className="text-destructive">*</span>
                                </label>
                                <Select
                                    id="netTermsDays"
                                    value={String(netTermsDays)}
                                    onChange={(e) => setNetTermsDays(Number(e.target.value))}
                                >
                                    {NET_TERMS_OPTIONS.map((d) => (
                                        <option key={d} value={d}>
                                            Net {d} — due {d} days after the invoice
                                        </option>
                                    ))}
                                </Select>
                                <p className="text-xs text-shell-foreground/60">
                                    Invoice due date is calculated as the invoice date plus this many days.
                                </p>
                            </div>
                        )}

                        {isEdit && (
                            <div className="space-y-3 rounded-md border border-border p-4">
                                <h3 className="text-sm font-semibold">Sales tax</h3>
                                <div className="space-y-1.5">
                                    <label htmlFor="taxStatus" className="text-sm font-medium">
                                        Tax status
                                    </label>
                                    <Select
                                        id="taxStatus"
                                        value={taxStatus}
                                        onChange={(e) => setTaxStatus(e.target.value)}
                                    >
                                        <option value="unknown">Not set (blocks invoice sends)</option>
                                        <option value="taxable">Taxable</option>
                                        <option value="exempt">Tax exempt</option>
                                    </Select>
                                    <p className="text-xs text-shell-foreground/60">
                                        Taxable customers get sales tax added to invoices and estimates automatically.
                                        &quot;Not set&quot; never guesses — sends are held until you decide.
                                    </p>
                                </div>
                                {taxStatus === "exempt" && (
                                    <>
                                        <div className="space-y-1.5">
                                            <label htmlFor="taxExemptReason" className="text-sm font-medium">
                                                Exemption reason
                                            </label>
                                            <Input
                                                id="taxExemptReason"
                                                value={taxExemptReason}
                                                onChange={(e) => setTaxExemptReason(e.target.value)}
                                                placeholder="e.g. Government entity, resale certificate"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label htmlFor="exemptionCertRef" className="text-sm font-medium">
                                                Exemption certificate reference
                                            </label>
                                            <Input
                                                id="exemptionCertRef"
                                                value={exemptionCertRef}
                                                onChange={(e) => setExemptionCertRef(e.target.value)}
                                                placeholder="Certificate # or file link"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                className="h-4 w-4 rounded border-input accent-primary"
                            />
                            <span className="text-sm font-medium">Active</span>
                        </label>

                        {isEdit && (
                            <div className="space-y-3 rounded-md border border-border p-4">
                                <h3 className="text-sm font-semibold">Billing address</h3>
                                <div className="space-y-1.5">
                                    <label htmlFor="billingLine1" className="text-sm font-medium">
                                        Address line 1
                                    </label>
                                    <Input
                                        id="billingLine1"
                                        value={billingLine1}
                                        onChange={(e) => setBillingLine1(e.target.value)}
                                        placeholder="123 Main St"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label htmlFor="billingLine2" className="text-sm font-medium">
                                        Address line 2
                                    </label>
                                    <Input
                                        id="billingLine2"
                                        value={billingLine2}
                                        onChange={(e) => setBillingLine2(e.target.value)}
                                        placeholder="Suite 100"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                    <div className="space-y-1.5 sm:col-span-2">
                                        <label htmlFor="billingCity" className="text-sm font-medium">
                                            City
                                        </label>
                                        <Input
                                            id="billingCity"
                                            value={billingCity}
                                            onChange={(e) => setBillingCity(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label htmlFor="billingState" className="text-sm font-medium">
                                            State
                                        </label>
                                        <Input
                                            id="billingState"
                                            value={billingState}
                                            onChange={(e) => setBillingState(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label htmlFor="billingZip" className="text-sm font-medium">
                                            ZIP
                                        </label>
                                        <Input
                                            id="billingZip"
                                            value={billingZip}
                                            onChange={(e) => setBillingZip(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {mutation.isError && (
                            <p className="text-sm text-destructive">Failed to save customer. Please try again.</p>
                        )}

                        <div className="flex items-center gap-3 pt-2">
                            <Button type="submit" disabled={mutation.isPending}>
                                <Save className="h-4 w-4" />
                                {mutation.isPending ? "Saving…" : isEdit ? "Update Customer" : "Create Customer"}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => navigate("/customers")}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
